import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCodeBranch,
  faCalendarAlt,
  faClock,
  faUserCheck,
  faUser,
  faComments,
  faStar,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import FalconCloseButton from 'components/common/FalconCloseButton';
import AppContext from 'context/Context';
import { formatDateTime } from './TicketCustomerSidebar/utils';

const MergeTicketsModal = ({
  show,
  onClose,
  client,
  currentTicket,
  existingTicket,
  onConfirmMerge,
  onAssignOnly,
  isProcessing = false
}) => {
  const context = useContext(AppContext);
  const isDark =
    context?.config?.isDark ??
    (typeof document !== 'undefined' &&
      (document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark')));

  if (!show || !existingTicket) return null;

  const currentTicketId = currentTicket?.idTicket || currentTicket?.id || 'N/A';
  const existingTicketId = existingTicket?.idTicket || existingTicket?.id || 'N/A';

  const clientId = client?.idClienteAkasia || client?.idCliente || client?.id || '';
  const clientName = client?.vNombreCompleto || client?.nombre || client?.vNombre || 'el cliente';
  const clientDisplayName = clientId ? `#${clientId} - ${clientName}` : clientName;

  const currentPhone = currentTicket?.vTelefono || currentTicket?.phone || currentTicket?.ticketPhone || '';
  const existingPhone = existingTicket?.vTelefono || '';

  const currentAgent = (currentTicket?.vNombreAgenteAsignado || currentTicket?.vNombreUsuarioAsignado || currentTicket?.assignedAgentName || '').trim();
  const existingAgent = (existingTicket?.vNombreAgenteAsignado || '').trim();

  const hasCurrentAgent = Boolean(currentAgent && currentAgent !== 'Sin asignar');
  const hasExistingAgent = Boolean(existingAgent && existingAgent !== 'Sin asignar');

  const currentSubject = currentTicket?.vAsunto || currentTicket?.subject || 'Sin asunto registrado';
  const existingSubject = existingTicket?.vAsunto || 'Sin asunto registrado';

  const existingEtiquetasList = existingTicket?.etiquetas && existingTicket.etiquetas.length > 0
    ? existingTicket.etiquetas
    : existingTicket?.vEtiquetas
      ? existingTicket.vEtiquetas.split(',').map(t => ({ vNombre: t.trim() })).filter(e => Boolean(e.vNombre))
      : [];

  const getInitials = name => {
    if (!name || name === 'Sin asignar') return '?';
    return name
      .trim()
      .split(' ')
      .filter(Boolean)
      .map(w => w[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header className="border-bottom-0 pb-1 pt-3 px-4">
        <FalconCloseButton onClick={onClose} disabled={isProcessing} className="ms-auto" />
      </Modal.Header>

      <Modal.Body className="pt-0 px-4">
        {/* Banner Superior de Detección */}
        <div
          className="p-3 mb-3 rounded-3"
          style={{
            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.08)' : '#fffbeb',
            border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7'}`
          }}
        >
          <div className="d-flex align-items-start gap-3">
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
                color: '#f59e0b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '1.2rem'
              }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div>
              <span
                className="badge rounded-pill mb-1"
                style={{
                  backgroundColor: isDark ? 'rgba(245, 158, 11, 0.25)' : '#fde68a',
                  color: isDark ? '#fef08a' : '#92400e',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}
              >
                COINCIDENCIA DE TICKET ACTIVO DETECTADA
              </span>
              <h5
                className="mb-1 fw-bold"
                style={{ color: isDark ? '#f8fafc' : '#0f172a' }}
              >
                El cliente ya cuenta con un caso activo en resolución
              </h5>
              <p
                className="mb-0 fs--1"
                style={{ color: isDark ? '#94a3b8' : '#475569', lineHeight: 1.4 }}
              >
                Al asignar a <strong style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}>{clientDisplayName}</strong>, el sistema identificó que este usuario mantiene el ticket <strong style={{ color: '#4f46e5' }}>#Ticket-{existingTicketId}</strong> en gestión activa. Fusionarlos unificará el hilo de mensajería y evitará duplicidad de esfuerzo técnico.
              </p>
            </div>
          </div>
        </div>

        {/* Comparación Visual: Dos Tarjetas Limpias Lado a Lado */}
        <div className="row g-3 mb-3 align-items-stretch position-relative">
          {/* Indicador de flecha centrado (Desktop) */}
          <div
            className="d-none d-md-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              pointerEvents: 'none'
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#4f46e5',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(79, 70, 229, 0.4)',
                border: `3px solid ${isDark ? '#1e293b' : '#ffffff'}`
              }}
            >
              <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.85rem' }} />
            </div>
          </div>
          {/* Tarjeta 1: Ticket Actual (Origen / Entrante) */}
          <div className="col-12 col-md-6">
            <div
              className="h-100 p-3 rounded-3 d-flex flex-column justify-content-between"
              style={{
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              <div>
                {/* Cabecera */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span
                    className="badge px-2 py-1"
                    style={{
                      backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : '#f1f5f9',
                      color: isDark ? '#94a3b8' : '#475569',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    Ticket Actual (Se cerrará)
                  </span>
                  <span
                    className="fw-bold fs-0"
                    style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}
                  >
                    #Ticket-{currentTicketId}
                  </span>
                </div>

                {/* Asunto */}
                <div className="mb-3">
                  <span
                    className="d-block text-uppercase fw-bold mb-1"
                    style={{ fontSize: '0.68rem', letterSpacing: '0.5px', color: isDark ? '#64748b' : '#94a3b8' }}
                  >
                    Asunto
                  </span>
                  <div
                    className="fw-bold fs--1"
                    style={{ color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1.35 }}
                  >
                    {currentSubject}
                  </div>
                </div>

                {/* Grid Canal & Estado */}
                <div
                  className="row g-2 p-2 rounded-2 mb-3"
                  style={{ backgroundColor: isDark ? 'rgba(15, 23, 42, 0.4)' : '#f8fafc' }}
                >
                  <div className="col-7">
                    <span
                      className="d-block text-uppercase fw-bold mb-1"
                      style={{ fontSize: '0.65rem', color: isDark ? '#64748b' : '#94a3b8' }}
                    >
                      Canal / Origen
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <FontAwesomeIcon icon={faComments} style={{ color: '#22c55e', fontSize: '0.85rem' }} />
                      <span className="fw-semibold fs--1" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                        {currentPhone || 'Sin número'}
                      </span>
                    </div>
                    <span
                      className="badge rounded-pill mt-1"
                      style={{
                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
                        color: isDark ? '#fde68a' : '#b45309',
                        fontSize: '0.62rem',
                        fontWeight: 600
                      }}
                    >
                      NÚMERO NUEVO
                    </span>
                  </div>

                  <div className="col-5">
                    <span
                      className="d-block text-uppercase fw-bold mb-1"
                      style={{ fontSize: '0.65rem', color: isDark ? '#64748b' : '#94a3b8' }}
                    >
                      Estado
                    </span>
                    <div className="d-flex align-items-center gap-1 mt-1">
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: currentTicket?.iEstadoTicket === 3 ? '#10b981' : '#f59e0b',
                          display: 'inline-block'
                        }}
                      />
                      <span className="fw-semibold fs--1" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                        {currentTicket?.iEstadoTicket === 3 ? 'En Progreso' : 'Abierto'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Agente asignado */}
                <div className="mb-2">
                  <span
                    className="d-block text-uppercase fw-bold mb-1"
                    style={{ fontSize: '0.65rem', color: isDark ? '#64748b' : '#94a3b8' }}
                  >
                    Agente Asignado
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    {hasCurrentAgent ? (
                      <>
                        <div
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            backgroundColor: isDark ? '#334155' : '#e2e8f0',
                            color: isDark ? '#cbd5e1' : '#475569',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getInitials(currentAgent)}
                        </div>
                        <span className="fs--1 fw-semibold" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                          {currentAgent}
                        </span>
                      </>
                    ) : (
                      <span
                        className="badge"
                        style={{
                          backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
                          color: isDark ? '#94a3b8' : '#64748b',
                          border: `1px dashed ${isDark ? '#475569' : '#cbd5e1'}`,
                          padding: '4px 8px',
                          fontSize: '0.72rem',
                          fontWeight: 500
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        Sin agente asignado
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer con fechas destacadas */}
              <div
                className="pt-2 mt-3 border-top"
                style={{ borderColor: isDark ? '#334155' : '#e2e8f0', fontSize: '0.78rem' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="d-flex align-items-center gap-1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-info" />
                    <span>Creado:</span>
                  </span>
                  <span className="fw-semibold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatDateTime(currentTicket?.dCreatedDate)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="d-flex align-items-center gap-1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    <FontAwesomeIcon icon={faClock} className="text-warning" />
                    <span>Última actividad:</span>
                  </span>
                  <span className="fw-semibold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatDateTime(currentTicket?.dUltimaActividad)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Indicador de flecha (Mobile) */}
          <div className="d-flex d-md-none justify-content-center my-1">
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#4f46e5',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3)'
              }}
            >
              <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.8rem' }} />
            </div>
          </div>

          {/* Tarjeta 2: Ticket Principal Activo (Destino) */}
          <div className="col-12 col-md-6">
            <div
              className="h-100 p-3 rounded-3 d-flex flex-column justify-content-between"
              style={{
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                border: `2px solid ${isDark ? '#4f46e5' : '#6366f1'}`,
                boxShadow: isDark
                  ? '0 4px 15px rgba(79, 70, 229, 0.15)'
                  : '0 4px 15px rgba(99, 102, 241, 0.1)'
              }}
            >
              <div>
                {/* Cabecera */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span
                    className="badge px-2 py-1"
                    style={{
                      backgroundColor: '#4f46e5',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: 700
                    }}
                  >
                    <FontAwesomeIcon icon={faStar} className="me-1" style={{ fontSize: '0.7rem' }} />
                    Ticket Principal (Se conservará)
                  </span>
                  <span
                    className="fw-bold fs-0"
                    style={{ color: '#4f46e5' }}
                  >
                    #Ticket-{existingTicketId}
                  </span>
                </div>

                {/* Asunto */}
                <div className="mb-3">
                  <span
                    className="d-block text-uppercase fw-bold mb-1"
                    style={{ fontSize: '0.68rem', letterSpacing: '0.5px', color: isDark ? '#64748b' : '#94a3b8' }}
                  >
                    Asunto
                  </span>
                  <div
                    className="fw-bold fs--1"
                    style={{ color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1.35 }}
                  >
                    {existingSubject}
                  </div>
                </div>

                {/* Grid Canal & Estado */}
                <div
                  className="row g-2 p-2 rounded-2 mb-3"
                  style={{
                    backgroundColor: isDark ? 'rgba(79, 70, 229, 0.08)' : '#f5f3ff',
                    border: `1px solid ${isDark ? 'rgba(99, 102, 241, 0.2)' : '#ede9fe'}`
                  }}
                >
                  <div className="col-7">
                    <span
                      className="d-block text-uppercase fw-bold mb-1"
                      style={{ fontSize: '0.65rem', color: isDark ? '#818cf8' : '#6366f1' }}
                    >
                      Canal / Contacto Oficial
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <FontAwesomeIcon icon={faComments} style={{ color: '#22c55e', fontSize: '0.85rem' }} />
                      <span className="fw-semibold fs--1" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                        {existingPhone || 'Sin número'}
                      </span>
                    </div>
                    <span
                      className="badge rounded-pill mt-1"
                      style={{
                        backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : '#dcfce7',
                        color: isDark ? '#86efac' : '#15803d',
                        fontSize: '0.62rem',
                        fontWeight: 600
                      }}
                    >
                      TELÉFONO REGISTRADO
                    </span>
                  </div>

                  <div className="col-5">
                    <span
                      className="d-block text-uppercase fw-bold mb-1"
                      style={{ fontSize: '0.65rem', color: isDark ? '#818cf8' : '#6366f1' }}
                    >
                      Estado
                    </span>
                    <div className="d-flex align-items-center gap-1 mt-1">
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: existingTicket?.iEstadoTicket === 3 ? '#10b981' : '#f59e0b',
                          display: 'inline-block'
                        }}
                      />
                      <span className="fw-semibold fs--1" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                        {existingTicket?.iEstadoTicket === 3 ? 'En Progreso' : 'Abierto'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Agente propietario */}
                <div className="mb-2">
                  <span
                    className="d-block text-uppercase fw-bold mb-1"
                    style={{ fontSize: '0.65rem', color: isDark ? '#64748b' : '#94a3b8' }}
                  >
                    Agente Asignado
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    {hasExistingAgent ? (
                      <>
                        <div
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            backgroundColor: '#4f46e5',
                            color: '#ffffff',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getInitials(existingAgent)}
                        </div>
                        <span className="fs--1 fw-semibold" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                          {existingAgent}
                        </span>
                      </>
                    ) : (
                      <span
                        className="badge"
                        style={{
                          backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
                          color: isDark ? '#94a3b8' : '#64748b',
                          border: `1px dashed ${isDark ? '#475569' : '#cbd5e1'}`,
                          padding: '4px 8px',
                          fontSize: '0.72rem',
                          fontWeight: 500
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        Sin agente asignado
                      </span>
                    )}
                  </div>
                </div>

                {/* Etiquetas e incidencias (si tiene) */}
                {existingEtiquetasList.length > 0 && (
                  <div className="mb-2 pt-2 border-top border-200">
                    <span
                      className="d-block text-uppercase fw-bold mb-1"
                      style={{ fontSize: '0.65rem', color: isDark ? '#64748b' : '#94a3b8' }}
                    >
                      Etiquetas e Incidencias:
                    </span>
                    <div className="d-flex flex-wrap gap-1">
                      {existingEtiquetasList.map((etq, idx) => (
                        <span
                          key={idx}
                          className="badge rounded-pill"
                          style={{
                            backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
                            color: isDark ? '#7dd3fc' : '#0369a1',
                            border: `1px solid ${isDark ? 'rgba(56, 189, 248, 0.3)' : '#bae6fd'}`,
                            fontSize: '0.68rem'
                          }}
                        >
                          {etq.vNombre || etq}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer con fechas destacadas */}
              <div
                className="pt-2 mt-3 border-top"
                style={{ borderColor: isDark ? '#334155' : '#e2e8f0', fontSize: '0.78rem' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="d-flex align-items-center gap-1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-info" />
                    <span>Creado:</span>
                  </span>
                  <span className="fw-semibold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatDateTime(existingTicket?.dCreatedDate)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="d-flex align-items-center gap-1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    <FontAwesomeIcon icon={faClock} className="text-warning" />
                    <span>Última actividad:</span>
                  </span>
                  <span className="fw-semibold" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatDateTime(existingTicket?.dUltimaActividad)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resumen de la acción de combinación */}
        <div
          className="p-3 rounded-3 mb-2"
          style={{
            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08)' : '#eff6ff',
            border: `1px dashed ${isDark ? 'rgba(59, 130, 246, 0.35)' : '#93c5fd'}`
          }}
        >
          <div className="d-flex align-items-start gap-2">
            <FontAwesomeIcon icon={faCodeBranch} className="text-primary mt-1 fs-0" />
            <div>
              <strong className="fs--1" style={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                Al confirmar la combinación de tickets:
              </strong>
              <ul className="mb-0 ps-3 mt-1 fs--1" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                <li>
                  Los mensajes del <strong>Ticket #{currentTicketId}</strong> se fusionarán cronológicamente en el <strong>Ticket #{existingTicketId}</strong>.
                </li>
                <li>Las etiquetas e incidencias se consolidarán sin duplicados.</li>
                <li>
                  El <strong>Ticket #{currentTicketId}</strong> se marcará como cerrado por combinación.
                </li>
                <li>
                  El número de origen (<strong>{currentPhone || 'actual'}</strong>) se registrará en los teléfonos adicionales del cliente.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer
        className="px-4 py-2.5 border-top d-flex justify-content-between align-items-center"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0'
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className="btn btn-sm px-3 shadow-none fw-semibold"
          style={{
            fontSize: '0.82rem',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            color: isDark ? '#38bdf8' : '#0284c7',
            border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
            transition: 'all 0.15s ease'
          }}
        >
          Cancelar
        </button>

        <div className="d-flex gap-2">
          {typeof onAssignOnly === 'function' && (
            <button
              type="button"
              onClick={onAssignOnly}
              disabled={isProcessing}
              title="Asigna el cliente manteniendo este ticket abierto por separado"
              className="btn btn-sm px-3 shadow-none fw-semibold"
              style={{
                fontSize: '0.82rem',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#38bdf8' : '#0284c7',
                border: '1px solid #0284c7',
                transition: 'all 0.15s ease'
              }}
            >
              Solo Asignar (Mantener separados)
            </button>
          )}

          <Button
            variant="primary"
            size="sm"
            onClick={onConfirmMerge}
            disabled={isProcessing}
            className="d-flex align-items-center gap-1.5 px-3 py-1 fw-semibold"
            style={{
              fontSize: '0.82rem',
              backgroundColor: '#0284c7',
              borderColor: '#0284c7'
            }}
          >
            {isProcessing ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                <span>Combinando...</span>
              </>
            ) : (
              <span>Combinar Tickets (Fusión a #{existingTicketId})</span>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

MergeTicketsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  client: PropTypes.object,
  currentTicket: PropTypes.object,
  existingTicket: PropTypes.object,
  onConfirmMerge: PropTypes.func.isRequired,
  onAssignOnly: PropTypes.func,
  isProcessing: PropTypes.bool
};

export default MergeTicketsModal;
